import {DataStatus} from 'platform/components';
import {HStack} from 'platform/foundation';

import {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';

import {clone, mergeDeepRight} from 'ramda';

import {selectTenant} from '@dms/api/core';
import {handleApiError} from '@dms/shared';
import {
  BillingInformation,
  BillingInformationService,
  CreateBillingInformationRequestBody,
  fetchBillingInformation,
  InlineEditingContextGlobalUpdateMethod,
  InlineEditingProvider,
  PutBillingInformationRequestBody,
  Tenant,
  useCallApi,
  useThunkDispatch,
} from '@dms/teas';

import {useOnMount} from 'shared/web';

import {BillingInfoCache} from '../types';
import {BankInformation} from './BankInformation';
import {BillingContactInformation} from './BillingContactInformation';

interface BillingInformationProps {
  billingInformationId?: string;
  setData?: (data: CreateBillingInformationRequestBody) => void;
}

export const BillingInformationDetail = ({
  billingInformationId,
  setData,
}: BillingInformationProps) => {
  const dispatch = useThunkDispatch();
  const [loading, setIsLoading] = useState(true);
  const {data: tenant} = useSelector(selectTenant);
  const [cachedBillingInfo, setCachedBillingInfo] = useState<BillingInfoCache>({
    // eslint-disable-next-line no-restricted-syntax
    data: {deputyPersons: []} as unknown as PutBillingInformationRequestBody,
    tenant: tenant as Tenant,
  });
  const isEdit = !!billingInformationId;

  const putBillingInformation = useCallApi(BillingInformationService.putBillingInformation);

  useOnMount(() => {
    if (!isEdit) {
      setIsLoading(false);
    }
  });

  useEffect(() => {
    if (billingInformationId) {
      dispatch(fetchBillingInformation(billingInformationId))
        .unwrap()
        .then((billingInformationData) => {
          setCachedBillingInfo((prev) => ({
            ...prev,
            data: clone(billingInformationData) as PutBillingInformationRequestBody,
          }));
        })
        .finally(() => setIsLoading(false));
    }
  }, [dispatch, billingInformationId]);

  const updateMethod = (data: PutBillingInformationRequestBody): Promise<BillingInformation> => {
    setCachedBillingInfo((prev) => ({
      ...prev,
      data: mergeDeepRight(prev.data, data) as PutBillingInformationRequestBody,
    }));
    setData && setData(cachedBillingInfo.data as CreateBillingInformationRequestBody);

    if (isEdit && billingInformationId) {
      const billing = {
        ...cachedBillingInfo.data,
        bankAccounts: cachedBillingInfo.data.bankAccounts?.map((bankAccount) => ({
          ...bankAccount,
          swift: bankAccount.swift || null,
          iban: bankAccount.iban || null,
        })),
      };

      // TODO: Change on BE might be needed (schemas) - isPrimary, personalIdCardType
      const response = putBillingInformation({
        billingInformationId,
        requestBody: billing,
      });

      response.catch((error: {response: Error}) => {
        handleApiError(error.response);
      });

      return response;
    }
    return Promise.resolve(cachedBillingInfo.data as BillingInformation);
  };

  return (
    <section>
      <InlineEditingProvider
        value={{
          updateMethod: updateMethod as InlineEditingContextGlobalUpdateMethod,
          editedValue: cachedBillingInfo,
        }}
      >
        <DataStatus isLoading={loading}>
          <HStack spacing={4}>
            <BillingContactInformation />
            <BankInformation />
          </HStack>
        </DataStatus>
      </InlineEditingProvider>
    </section>
  );
};
